// Hook for fetching storage quota information
import { useState, useEffect, useCallback } from 'react';
import type { StorageQuota } from '@/lib/local-types';
import { getStoredVideos } from '@/features/videos/videos-store';

interface UseStorageQuotaReturn {
  quota: StorageQuota | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useStorageQuota(): UseStorageQuotaReturn {
  const [quota, setQuota] = useState<StorageQuota | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuota = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const videos = await getStoredVideos();
      const used = videos.reduce((sum, video) => sum + video.size, 0);
      const quota = 1024 * 1024 * 1024;
      setQuota({
        used,
        quota,
        videoCount: videos.length,
        percentUsed: quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load local storage usage';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  return {
    quota,
    isLoading,
    error,
    refresh: fetchQuota,
  };
}
